import { useState, useEffect } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PaginationState } from '@tanstack/react-table';
import { PlusCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { RecordingDetailsModal, RecordingDetails } from './components/RecordingDetailsModal';
import { TranscriptListItem } from './components/TranscriptListItem';
import { TrackingEvents, PageName } from '@/analytics/events';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useCreateTranscript } from '@/api/mutations/useCreateTranscript';
import { useGetTranscripts } from '@/api/queries/useGetTranscripts';
import { Button } from '@/components/atoms/new-button';
import EmptyStateCreation from '@/components/molecules/EmptyStateCreation';
import Pagination from '@/components/organisms/Pagination';
import { CountsChip } from '@/screens/Chronos/CaseEditor/DataView/components/CountsChip';

const TranscriptList = () => {
  const [showRecordingDetailsModal, setShowNewRecordingModal] = useState(false);

  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const caseId = searchParams.get('caseId');

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const { data: transcriptData, isFetching: isLoadingTranscripts } = useGetTranscripts(
    caseId,
    pagination.pageIndex + 1,
    pagination.pageSize,
  );

  const createTranscriptMutation = useCreateTranscript();

  const transcripts = transcriptData?.transcripts ?? [];
  const pageCount = transcriptData?.pageCount ?? 0;
  const totalCount = transcriptData?.totalCount ?? 0;

  // Track page view
  useEffect(() => {
    trackPageView(PageName.TranscriptList);
  }, []);

  const handleNewRecordingClick = () => {
    trackCustomEvent(TrackingEvents.ClickNewRecordingButton);
    setShowNewRecordingModal(true);
  };

  const createNewTranscript = async (details: RecordingDetails) => {
    if (!caseId) return;

    const newTranscript = await createTranscriptMutation.mutateAsync({
      caseId,
      name: details.name,
      objective: details.objective,
      deponent: details.deponent,
      otherSpeakers: details.otherSpeakers,
      phraseList: details.phraseList,
    });

    setShowNewRecordingModal(false);

    navigate(`/app/chronos/case-editor/transcripts/${newTranscript.id}?caseId=${caseId}`);
  };

  const playTranscript = (transcriptId: string) => {
    navigate(`/app/chronos/case-editor/transcripts/${transcriptId}?caseId=${caseId}`);
  };

  const goToPage = (pageIndex: number) => {
    setPagination({ pageIndex, pageSize: pagination.pageSize });
  };

  const nextPage = () => {
    if (pagination.pageIndex < pageCount - 1) {
      goToPage(pagination.pageIndex + 1);
    }
  };

  const prevPage = () => {
    if (pagination.pageIndex > 0) {
      goToPage(pagination.pageIndex - 1);
    }
  };

  return (
    <div className="h-full w-full overflow-x-auto overflow-y-hidden border-b transition-all duration-300">
      <div className="flex h-full min-w-full flex-col">
        <div className="mb-4 flex items-center justify-between border-b border-gray-200 pt-4 pb-4">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold">Transcripts</h1>
            <CountsChip count={totalCount} label="transcripts" />
            <div className="h-6 w-px bg-gray-300" />

            <Button onClick={handleNewRecordingClick} variant="primary" size="sm" className="h-8 text-xs">
              <PlusCircle className="h-3 w-3" />
              New Recording
            </Button>
          </div>

          {pageCount > 1 && (
            <Pagination
              canGetPrevPage={pagination.pageIndex > 0}
              canGetNextPage={pagination.pageIndex < pageCount - 1}
              prevPage={prevPage}
              nextPage={nextPage}
              currentPage={pagination.pageIndex}
              noOfPages={pageCount}
              goToPage={goToPage}
            />
          )}
        </div>

        <div className="flex h-full overflow-hidden pb-2">
          <div className="h-full w-full overflow-y-auto pr-2">
            {isLoadingTranscripts ? (
              <div className="flex h-full w-full items-center justify-center">
                <FontAwesomeIcon icon={faCircleNotch} className="fa-spin" />
              </div>
            ) : transcripts.length > 0 ? (
              <div className="flex h-full flex-col gap-2 pt-1 pb-6">
                {transcripts.map((transcript) => (
                  <TranscriptListItem
                    key={transcript.id}
                    transcript={transcript}
                    onPlay={playTranscript}
                    caseId={caseId || ''}
                  />
                ))}
              </div>
            ) : (
              <EmptyStateCreation
                title="No transcripts found"
                subtitle="Start your first recording to create a transcript."
                onClick={handleNewRecordingClick}
              />
            )}
          </div>
        </div>
      </div>

      <RecordingDetailsModal
        isOpen={showRecordingDetailsModal}
        onClose={() => setShowNewRecordingModal(false)}
        onConfirm={createNewTranscript}
        isLoading={createTranscriptMutation.isLoading}
      />
    </div>
  );
};

export default TranscriptList;
